import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
const NAMES = ['María', 'Ana', 'Sofía', 'Valentina', 'Camila', 'Isabella', 'Lucía', 'Gabriela', 'Daniela', 'Paula', 'Carolina', 'Andrea', 'Natalia', 'Fernanda', 'Alejandra', 'Mariana', 'Laura', 'Diana', 'Catalina', 'Juliana', 'Paola', 'Claudia', 'Mónica', 'Patricia', 'Sandra', 'Rosa', 'Carmen', 'Elena', 'Verónica', 'Adriana', 'Lorena', 'Silvia', 'Beatriz', 'Rocío', 'Alicia'];
const CITIES = ['Ciudad de México', 'Bogotá', 'Lima', 'Buenos Aires', 'Santiago', 'Quito', 'Medellín', 'Guadalajara', 'Monterrey', 'Cali', 'Barranquilla', 'Cartagena', 'Cancún', 'Puebla', 'Arequipa', 'Guayaquil', 'Córdoba', 'Rosario', 'Valparaíso', 'Cusco', 'Santo Domingo', 'Panamá', 'San José', 'Tegucigalpa', 'Managua'];
interface ScannedProduct {
  title: string;
  editionLabel: string;
  priceNumber: number;
  priceText: string;
  thumbnailUrl: string;
}
const DEFAULT_PRODUCT: ScannedProduct = {
  title: 'Ilumina y Crea',
  editionLabel: 'Acceso de por vida',
  priceNumber: 52.49,
  priceText: '$52.49 USD',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/pFuqYVnB8YN26xHHnZL6va/mk-1.png'
};
const getRandomInt = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;
const scanDOMForProducts = (): ScannedProduct[] => {
  const products: ScannedProduct[] = [];
  const priceRegex = /\$\s?\d+(\.\d{2})?/;
  // Try JSON-LD first
  const scripts = document.querySelectorAll('script[type="application/ld+json"]');
  scripts.forEach(script => {
    try {
      const data = JSON.parse(script.textContent || '');
      if (data['@type'] === 'Product' || data['@type'] === 'Course') {
        products.push({
          title: data.name || 'Ilumina y Crea',
          editionLabel: 'Acceso Completo',
          priceNumber: data.offers?.price ? parseFloat(data.offers.price) : 52.49,
          priceText: data.offers?.price ? `$${data.offers.price} USD` : '$52.49 USD',
          thumbnailUrl: data.image || DEFAULT_PRODUCT.thumbnailUrl
        });
      }
    } catch {
      // Ignore
    }
  });
  // Fallback: scan DOM for prices
  if (products.length === 0) {
    const allElements = Array.from(document.querySelectorAll('*'));
    const priceElements = allElements.filter(el => el.children.length === 0 && priceRegex.test(el.textContent || ''));
    if (priceElements.length > 0) {
      const match = priceElements.find(el => el.textContent?.includes('52.49'))?.textContent?.match(priceRegex)?.[0] || priceElements[0].textContent?.match(priceRegex)?.[0] || '$52.49';
      const priceNumber = parseFloat(match.replace(/[^0-9.]/g, ''));
      const img = document.querySelector('img[src*="mk-1"]') as HTMLImageElement | null;
      products.push({
        title: 'Ilumina y Crea',
        editionLabel: 'Acceso de por vida',
        priceNumber,
        priceText: `${match} USD`,
        thumbnailUrl: img?.src || DEFAULT_PRODUCT.thumbnailUrl
      });
    }
  }
  return products;
};
interface ToastData {
  id: number;
  name: string;
  city: string;
  product: ScannedProduct;
  minutesAgo: number;
}
export function RecentPurchaseToast() {
  const [toast, setToast] = useState<ToastData | null>(null);
  const [catalog, setCatalog] = useState<ScannedProduct[]>([]);
  const usedNamesRef = useRef<string[]>([]);
  const toastIdCounter = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shouldReduceMotion = useReducedMotion();
  useEffect(() => {
    const timer = setTimeout(() => {
      const scanned = scanDOMForProducts();
      setCatalog(scanned);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);
  const getNextToastData = useCallback((): ToastData => {
    let availableNames = NAMES.filter(n => !usedNamesRef.current.includes(n));
    if (availableNames.length === 0) {
      availableNames = NAMES;
      usedNamesRef.current = [];
    }
    const name = availableNames[getRandomInt(0, availableNames.length - 1)];
    usedNamesRef.current.push(name);
    if (usedNamesRef.current.length > 10) {
      usedNamesRef.current.shift();
    }
    const city = CITIES[getRandomInt(0, CITIES.length - 1)];
    const product = catalog.length > 0 ? catalog[getRandomInt(0, catalog.length - 1)] : DEFAULT_PRODUCT;
    toastIdCounter.current += 1;
    return {
      id: toastIdCounter.current,
      name,
      city,
      product,
      minutesAgo: getRandomInt(1, 15)
    };
  }, [catalog]);
  const scheduleNext = useCallback((isFirst: boolean = false) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    const delay = isFirst ? 3000 : getRandomInt(8000, 15000);
    timeoutRef.current = setTimeout(() => {
      setToast(getNextToastData());
      hideTimeoutRef.current = setTimeout(() => {
        setToast(null);
        scheduleNext(false);
      }, 4000);
    }, delay);
  }, [getNextToastData]);
  useEffect(() => {
    scheduleNext(true);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, [scheduleNext]);
  return <div className="fixed bottom-24 md:bottom-28 left-0 right-0 z-[100] flex justify-center pointer-events-none px-4" data-id="element-414">
      <AnimatePresence mode="wait" data-id="element-415">
        {toast && <motion.div key={toast.id} initial={shouldReduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 50
      }} animate={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 1,
        y: 0
      }} exit={shouldReduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 20
      }} transition={{
        type: 'spring',
        stiffness: 400,
        damping: 30
      }} className="bg-white rounded-2xl shadow-2xl border border-[#C4A882]/30 p-3 pr-5 flex items-center gap-3 max-w-sm w-full pointer-events-auto" data-id="element-416">
            {/* Thumbnail */}
            <div className="w-14 h-14 flex-shrink-0 rounded-xl overflow-hidden bg-[#FDF8F4] border border-[#C4A882]/20" data-id="element-417">
              <img src={toast.product.thumbnailUrl} alt={toast.product.title} className="w-full h-full object-cover" data-id="element-418" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0" data-id="element-419">
              <p className="text-sm text-[#5C4033] leading-tight mb-1" data-id="element-420">
                <span className="font-bold text-[#9B2335]" data-id="element-421">{toast.name}</span>{' '}
                en {toast.city} acaba de inscribirse
              </p>
              <div className="flex items-center gap-2" data-id="element-422">
                <span className="text-[10px] uppercase tracking-wider font-semibold text-[#C4A882] bg-[#FDF8F4] px-1.5 py-0.5 rounded" data-id="element-423">
                  {toast.product.editionLabel}
                </span>
                <span className="text-xs font-bold text-[#9B2335]" data-id="element-424">
                  {toast.product.priceText}
                </span>
              </div>
              <p className="text-[10px] text-[#5C4033]/50 mt-1" data-id="element-425">
                Hace {toast.minutesAgo}{' '}
                {toast.minutesAgo === 1 ? 'minuto' : 'minutos'}
              </p>
            </div>

            {/* Verified dot */}
            <div className="flex-shrink-0" data-id="element-426">
              <div className="w-2.5 h-2.5 rounded-full bg-green-500 shadow-sm shadow-green-500/50" data-id="element-427" />
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}